import { useEffect, useRef } from 'react';

const STYLE_GUARD_ID = 'mm-forced-dark-guard';
const LOCK_CLASS = 'mm-forced-dark-locked';
const DISABLED_ATTR = 'data-mm-dark-disabled';
const PREV_MEDIA_ATTR = 'data-mm-dark-prev-media';

/**
 * Bloqueio de página inteira para /display/* e /styles.
 * NÃO usar meta darkreader-lock — ao remover, o Dark Reader não religa.
 */

type InlineSnapshot = {
  htmlStyle: string | null;
  bodyStyle: string | null;
  htmlClass: string | null;
  bodyClass: string | null;
  htmlAttrs: Record<string, string>;
  bodyAttrs: Record<string, string>;
};

const DARKREADER_ATTRS = [
  'data-darkreader-mode',
  'data-darkreader-scheme',
  'data-darkreader-proxy-injected',
  'data-theme',
  'data-color-scheme',
];

function isForcedDarkStyleNode(node: Element): boolean {
  if (node.id === STYLE_GUARD_ID) {
    return false;
  }
  const id = (node.id || '').toLowerCase();
  const className = String((node as HTMLElement).className || '').toLowerCase();
  const media = (node.getAttribute('media') || '').toLowerCase();

  if (
    id.includes('darkreader') ||
    id.includes('dark-reader') ||
    id.includes('nighteye') ||
    id.includes('night-eye') ||
    id.includes('dark-mode') ||
    id.includes('darkmode')
  ) {
    return true;
  }
  if (
    className.includes('darkreader') ||
    className.includes('dark-reader') ||
    className.includes('nighteye') ||
    className.includes('night-eye')
  ) {
    return true;
  }
  if (media.includes('prefers-color-scheme') && media.includes('dark')) {
    return true;
  }
  if (node instanceof HTMLStyleElement) {
    const text = (node.textContent || '').toLowerCase();
    if (text.includes('darkreader') || text.includes('--darkreader')) {
      return true;
    }
  }
  return false;
}

function pauseForcedDarkStyles(): void {
  document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
    if (!isForcedDarkStyleNode(node)) {
      return;
    }
    if (!(node instanceof HTMLStyleElement) && !(node instanceof HTMLLinkElement)) {
      return;
    }
    if (node.getAttribute(DISABLED_ATTR) === '1') {
      return;
    }
    node.setAttribute(DISABLED_ATTR, '1');
    node.setAttribute(PREV_MEDIA_ATTR, node.getAttribute('media') || '');
    node.setAttribute('media', 'not all');
    if ('disabled' in node) {
      node.disabled = true;
    }
  });
}

function resumeForcedDarkStyles(): void {
  document.querySelectorAll(`[${DISABLED_ATTR}="1"]`).forEach((node) => {
    if (!(node instanceof HTMLStyleElement) && !(node instanceof HTMLLinkElement)) {
      return;
    }
    const prevMedia = node.getAttribute(PREV_MEDIA_ATTR);
    if (prevMedia !== null && prevMedia !== '') {
      node.setAttribute('media', prevMedia);
    } else {
      node.removeAttribute('media');
    }
    node.removeAttribute(PREV_MEDIA_ATTR);
    node.removeAttribute(DISABLED_ATTR);
    if ('disabled' in node) {
      node.disabled = false;
    }
  });
}

function ensureGuardStylesheet(): void {
  let style = document.getElementById(STYLE_GUARD_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_GUARD_ID;
    document.head.appendChild(style);
  }

  style.textContent = `
html.${LOCK_CLASS},
html.${LOCK_CLASS} body,
html.${LOCK_CLASS} #root {
  color-scheme: only light !important;
  filter: none !important;
  -webkit-filter: none !important;
  forced-color-adjust: none !important;
  -webkit-forced-color-adjust: none !important;
}
html.${LOCK_CLASS} {
  background-color: #000000;
}
`;
}

function removeGuardStylesheet(): void {
  document.getElementById(STYLE_GUARD_ID)?.remove();
}

function captureAttrs(el: HTMLElement, names: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  names.forEach((name) => {
    const val = el.getAttribute(name);
    if (val !== null) {
      out[name] = val;
    }
  });
  return out;
}

function captureInlineSnapshot(): InlineSnapshot {
  const html = document.documentElement;
  const body = document.body;
  return {
    htmlStyle: html.getAttribute('style'),
    bodyStyle: body ? body.getAttribute('style') : null,
    htmlClass: html.getAttribute('class'),
    bodyClass: body ? body.getAttribute('class') : null,
    htmlAttrs: captureAttrs(html, DARKREADER_ATTRS),
    bodyAttrs: body ? captureAttrs(body, DARKREADER_ATTRS) : {},
  };
}

function restoreInlineSnapshot(snapshot: InlineSnapshot | null): void {
  const html = document.documentElement;
  const body = document.body;

  html.classList.remove(LOCK_CLASS);

  if (!snapshot) {
    return;
  }

  if (snapshot.htmlStyle === null) {
    html.removeAttribute('style');
  } else {
    html.setAttribute('style', snapshot.htmlStyle);
  }

  if (snapshot.htmlClass === null) {
    html.removeAttribute('class');
  } else {
    html.setAttribute('class', snapshot.htmlClass);
  }

  DARKREADER_ATTRS.forEach((name) => {
    if (name in snapshot.htmlAttrs) {
      html.setAttribute(name, snapshot.htmlAttrs[name]);
    } else {
      html.removeAttribute(name);
    }
  });

  if (body) {
    if (snapshot.bodyStyle === null) {
      body.removeAttribute('style');
    } else {
      body.setAttribute('style', snapshot.bodyStyle);
    }

    if (snapshot.bodyClass === null) {
      body.removeAttribute('class');
    } else {
      body.setAttribute('class', snapshot.bodyClass);
    }

    DARKREADER_ATTRS.forEach((name) => {
      if (name in snapshot.bodyAttrs) {
        body.setAttribute(name, snapshot.bodyAttrs[name]);
      } else {
        body.removeAttribute(name);
      }
    });
  }
}

function applyDocumentLockOverrides(): void {
  const html = document.documentElement;
  const body = document.body;

  html.classList.add(LOCK_CLASS);
  html.style.setProperty('filter', 'none', 'important');
  html.style.setProperty('color-scheme', 'only light', 'important');

  body?.style.setProperty('filter', 'none', 'important');
  body?.style.setProperty('color-scheme', 'only light', 'important');

  DARKREADER_ATTRS.forEach((attr) => {
    html.removeAttribute(attr);
    body?.removeAttribute(attr);
  });

  html.classList.remove('dark', 'darkreader', 'dark-mode', 'nightmode', 'night-mode');
  body?.classList.remove('dark', 'darkreader', 'dark-mode', 'nightmode', 'night-mode');
}

function stripStickyLockMetas(): void {
  document.querySelectorAll('meta[name="darkreader-lock"]').forEach((el) => el.remove());
  document.getElementById('mm-display-darkreader-lock')?.remove();
  document.getElementById('mm-display-color-scheme')?.remove();
  document.getElementById('mm-display-supported-color-schemes')?.remove();
}

/**
 * Visualização (/display/:id, /display/televisao/:id) e editor (/styles):
 * bloqueia dark forçado na página inteira. Ao sair, restaura o dark do usuário.
 */
export function useDisplayForcedDarkLock(enabled: boolean): void {
  const snapshotRef = useRef<InlineSnapshot | null>(null);

  useEffect(() => {
    stripStickyLockMetas();

    if (!enabled) {
      resumeForcedDarkStyles();
      removeGuardStylesheet();
      restoreInlineSnapshot(snapshotRef.current);
      snapshotRef.current = null;
      return;
    }

    if (!snapshotRef.current) {
      snapshotRef.current = captureInlineSnapshot();
    }

    let applying = false;

    const applyLock = () => {
      if (applying) {
        return;
      }
      applying = true;
      try {
        stripStickyLockMetas();
        ensureGuardStylesheet();
        applyDocumentLockOverrides();
        pauseForcedDarkStyles();
      } finally {
        requestAnimationFrame(() => {
          applying = false;
        });
      }
    };

    applyLock();

    const observer = new MutationObserver(() => {
      applyLock();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class', ...DARKREADER_ATTRS],
    });

    observer.observe(document.head, {
      childList: true,
      subtree: true,
    });

    if (document.body) {
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['style', 'class', ...DARKREADER_ATTRS],
      });
    }

    const t1 = window.setTimeout(applyLock, 50);
    const t2 = window.setTimeout(applyLock, 250);
    const t3 = window.setTimeout(applyLock, 1000);

    return () => {
      observer.disconnect();
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);

      resumeForcedDarkStyles();
      removeGuardStylesheet();
      restoreInlineSnapshot(snapshotRef.current);
      snapshotRef.current = null;
    };
  }, [enabled]);
}
